/**
 * Relatie-import (FO-092, batch-23) — gedeeld per-rij Zod-schema + report-types.
 *
 * Eén bron voor de validatie van een geïmporteerde Excel-rij: de API
 * (`POST /clients/import`) valideert er elke rij mee en de backoffice toont het
 * per-rij-resultaat met dezelfde types. Uitsluitend klant-velden - geen
 * kostprijs/marge (die staan niet in een relatie-record).
 */

import { z } from 'zod'

// ─── Per-rij invoerschema ──────────────────────────────────────────────────
//
// De parser levert per rij een `Record<string, string>` met alléén de gevulde
// cellen (lege cellen ontbreken). Daarom is elk veld behalve `name` optioneel:
// een ontbrekend veld bij een update overschrijft niets ("data-integer").

/** Lege/whitespace-cel telt als "niet aanwezig" (undefined) i.p.v. lege string. */
const emptyToUndefined = (v: unknown): unknown => {
  if (typeof v !== 'string') return v
  const t = v.trim()
  return t === '' ? undefined : t
}

const optionalText = z.preprocess(emptyToUndefined, z.string().optional())

const RELATIE_IMPORT_TYPES = ['KLANT', 'PROSPECT', 'LEVERANCIER', 'PARTNER', 'INTERN'] as const
const RELATIE_IMPORT_STATUSES = ['ACTIEF', 'INACTIEF'] as const

/** Enum-cel hoofdletter-ongevoelig inlezen (bv. "klant" -> "KLANT"). */
const enumUpper = (v: unknown): unknown =>
  typeof v === 'string' && v.trim() !== '' ? v.trim().toUpperCase() : undefined

export const relatieImportRowSchema = z.object({
  name: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim() : v),
    z.string({ error: 'Naam is verplicht' }).min(1, 'Naam is verplicht'),
  ),
  clientNumber: optionalText,
  type: z.preprocess(
    enumUpper,
    z
      .enum(RELATIE_IMPORT_TYPES, {
        error: 'Type moet KLANT, PROSPECT, LEVERANCIER, PARTNER of INTERN zijn',
      })
      .optional(),
  ),
  status: z.preprocess(
    enumUpper,
    z
      .enum(RELATIE_IMPORT_STATUSES, {
        error: 'Status moet ACTIEF of INACTIEF zijn',
      })
      .optional(),
  ),
  email: z.preprocess(emptyToUndefined, z.string().email('Ongeldig e-mailadres').optional()),
  phone: optionalText,
  street: optionalText,
  houseNumber: optionalText,
  postalCode: optionalText,
  city: optionalText,
  country: optionalText,
  kvkNumber: z.preprocess(
    emptyToUndefined,
    z.string().regex(/^\d{8}$/, 'KVK-nummer moet 8 cijfers zijn').optional(),
  ),
  vatNumber: optionalText,
  iban: optionalText,
})

export type RelatieImportRow = z.output<typeof relatieImportRowSchema>

// ─── Per-rij-resultaat (report) ──────────────────────────────────────────────

export type RelatieImportRowStatus = 'created' | 'updated' | 'skipped' | 'error'

export interface RelatieImportFieldError {
  field: string
  message: string
}

export interface RelatieImportRowResult {
  /** 1-gebaseerd rijnummer uit het Excel-bestand (rij 1 = header). */
  row: number
  status: RelatieImportRowStatus
  /** Id van de aangemaakte/bijgewerkte relatie (bij created/updated). */
  clientId?: string
  /** Korte samenvatting (foutmelding of toelichting). */
  message?: string
  /** Per-veld validatiefouten (bij status 'error' uit een schema-mismatch). */
  fieldErrors?: RelatieImportFieldError[]
}

export interface RelatieImportSummary {
  total: number
  created: number
  updated: number
  skipped: number
  errors: number
}

export interface RelatieImportReport {
  summary: RelatieImportSummary
  rows: RelatieImportRowResult[]
}
